import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { setUserTokenFromOutside, logoutFromOutside } from '../contexts/UserContext';
import { jwtDecode } from 'jwt-decode';
import { serverStatusService } from '../services/serverStatusService';

export const API_URL = process.env.REACT_APP_API_HOST || 'http://localhost:3000';

// Create a simple token manager with aggressive refresh locking
let currentToken: string | null = null;
let refreshTimer: NodeJS.Timeout | null = null;
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

// Global refresh lock - more aggressive than the promise approach
let globalRefreshLock = false;
let lastRefreshTime = 0;

export const setAuthToken = (token: string | null) => {
  currentToken = token;
  
  // Clear existing timer
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  
  // Set up proactive refresh if we have a token
  if (token && isNotExpired(token)) {
    setupProactiveRefresh(token);
  }
};

export const getAuthToken = () => currentToken;


const handleTokenExpiry = () => {
  // Clear refresh timer
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  
  // Reset ALL refresh state
  isRefreshing = false;
  refreshPromise = null;
  globalRefreshLock = false;
  lastRefreshTime = 0;
  
  setAuthToken(null);
  localStorage.removeItem('user');
  
  // Notify UserContext to update state
  logoutFromOutside();
}

const isNotExpired = (token: string) => {
  const decoded = jwtDecode(token);
  return decoded.exp && decoded.exp > Date.now() / 1000;
};

const setupProactiveRefresh = (token: string) => {
  try {
    const decoded = jwtDecode(token);
    if (!decoded.exp) return;
    
    const expiryTime = decoded.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const timeUntilExpiry = expiryTime - currentTime;
    
    // For tokens lasting more than 2 minutes, refresh 2 minutes before expiry
    // For shorter tokens (like our 1-minute tokens), refresh at 50% of lifetime
    let refreshTime: number;
    if (timeUntilExpiry > 2 * 60 * 1000) {
      refreshTime = timeUntilExpiry - (2 * 60 * 1000); // 2 minutes before
    } else {
      refreshTime = timeUntilExpiry * 0.5; // 50% of token lifetime
    }
    
    // Don't set refresh time if it's too soon or already past
    if (refreshTime > 5000) { // At least 5 seconds from now
      refreshTimer = setTimeout(async () => {
        // Don't proactively refresh if we're already refreshing
        if (isRefreshing) {
          return;
        }
        
        try {
          const newToken = await refreshToken();
        } catch (error) {
          // Don't handle the error here - let the 401 interceptor handle it on next request
        }
      }, refreshTime);
    }
  } catch (error) {
  }
};

export const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 60000,
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token && isNotExpired(token)) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  response => {
    // Server is reachable
    serverStatusService.notifyServerReachable();
    return response;
  },
  async error => {
    const originalRequest = error.config;

    console.log('originalRequest', originalRequest);
    console.log('error', error);

    // Check if it's a network error (server unreachable)
    if (!error.response && (error.code === 'ECONNREFUSED' || error.code === 'NETWORK_ERROR' || error.message.includes('Network Error'))) {
      console.error('Server unreachable:', error.message);
      serverStatusService.notifyServerError();
      return Promise.reject(error);
    }

    // Don't retry login or refresh requests
    if (originalRequest.url === '/auth/login' || originalRequest.url === '/auth/refresh') {
      return Promise.reject(error);
    }

    // Handle 401 responses (both token expired and general unauthorized)
    if (error?.response?.status === 401) {
      console.log(`🚨 401 detected for ${originalRequest.url}`);
      
      // Prevent infinite retry loops
      if (originalRequest._retry) {
        console.log('🚫 Token refresh failed twice, logging out user');
        handleTokenExpiry();
        return Promise.reject(new Error('You have been logged out'));
      }

      // AGGRESSIVE: Check if we've refreshed recently (within last 2 seconds)
      const now = Date.now();
      if (now - lastRefreshTime < 2000) {
        console.log(`⏰ ${originalRequest.url} - Recent refresh detected, rejecting immediately`);
        return Promise.reject(new Error('Recent refresh failed'));
      }

      // AGGRESSIVE: Check global lock first
      if (globalRefreshLock) {
        console.log(`🔒 ${originalRequest.url} - Global refresh lock active, rejecting`);
        return Promise.reject(new Error('Refresh in progress'));
      }

      originalRequest._retry = true;

      // Set global lock immediately
      globalRefreshLock = true;
      console.log(`🔐 ${originalRequest.url} - Set global refresh lock`);

      try {
        console.log(`🚀 ${originalRequest.url} - Starting refresh process`);
        const newToken = await refreshToken();
        lastRefreshTime = Date.now();
        
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        console.log(`✅ ${originalRequest.url} - Refresh successful, retrying request`);
        return axiosInstance(originalRequest);
        
      } catch (refreshError) {
        console.log(`❌ ${originalRequest.url} - Refresh failed:`, refreshError);
        handleTokenExpiry();
        return Promise.reject(new Error('You have been logged out'));
      } finally {
        // Release global lock
        globalRefreshLock = false;
        console.log(`🔓 ${originalRequest.url} - Released global refresh lock`);
      }
    }

    return Promise.reject(error);
  }
);

const get = async <T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return axiosInstance.get<T>(url, config);
};

const post = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return axiosInstance.post<T>(url, data, config);
};

const patch = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return axiosInstance.patch<T>(url, data, config);
};

const put = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return axiosInstance.put<T>(url, data, config);
};

const remove = async <T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return axiosInstance.delete<T>(url, config);
};

export const refreshToken = async () => {
  console.log('🔄 refreshToken() called - checking if already refreshing:', isRefreshing);
  
  // Double-check if we're already refreshing
  if (isRefreshing) {
    console.log('⏳ Already refreshing, throwing error to prevent duplicate');
    throw new Error('Already refreshing');
  }
  
  isRefreshing = true;
  console.log('🚀 Starting token refresh');
  
  try {
    // Create a new axios instance for refresh to avoid interceptor conflicts
    const refreshResponse = await axios.post(`${API_URL}/auth/refresh`, {}, { 
      withCredentials: true,
      timeout: 10000 // Short timeout for refresh
    });
    
    if (refreshResponse?.status === 200) {
      const newToken = refreshResponse.data.access_token;
      console.log('✅ Token refresh successful, setting new token');
      setAuthToken(newToken); // This will also setup proactive refresh
      setUserTokenFromOutside(newToken);
      isRefreshing = false;
      return newToken;
    } else {
      isRefreshing = false;
      throw new Error('Unable to refresh token');
    }
  } catch (error) {
    isRefreshing = false;
    throw error;
  }
};

export default { get, post, patch, put, remove };
